/**
 * EnvelopeSelector Component
 * ==========================
 * FINLIST ENVELOPE SYSTEM: Phase 3 - Wire Transactions & Shopping
 * 
 * A searchable dropdown for selecting budget envelopes.
 * - Shows all envelopes from the active budget
 * - Search filters by name (2+ characters)
 * - Displays envelope balance/remaining info
 * - Links to Budget page if no envelopes exist
 * 
 * GUARDRAILS:
 * - This component ONLY selects envelopes, never creates them
 * - Envelope creation is restricted to Budget page only
 * 
 * @version 1.0.0
 * @date 2025-11-29
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, ChevronDown, AlertCircle, Plus, Wallet, Check, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useBudgetEnvelopes, useEnvelopeSearch } from '@/context/CategoryEnvelopeContext';
import { getCurrencySymbol, formatCurrency } from '@/utils/budgetHelpers';
import { useAuth } from '@/contexts/AuthContext';
import { toTitleCase } from '@/lib/utils';
// CATEGORY CORE SSOT: Use single source of truth for category names
import { getCategoryDisplayName, normalizeToCategoryTaxonomyId } from '@/constants/categoryCore';

/**
 * Format envelope name - uses categoryCore.js as single source of truth
 * @param {Object} envelope - The envelope object
 * @returns {string} Properly formatted name from canonical source
 */
function formatEnvelopeName(envelope) {
  if (!envelope) return 'Unnamed Envelope';
  
  // Try to get canonical name using categoryCore.js SSOT
  const categoryId = envelope.categoryKey || envelope.categoryId;
  if (categoryId) {
    // First normalize, then get display name
    const canonicalId = normalizeToCategoryTaxonomyId(categoryId);
    if (canonicalId) {
      const canonicalName = getCategoryDisplayName(canonicalId);
      if (canonicalName && canonicalName !== 'Unknown') {
        return canonicalName;
      }
    }
    // Try original ID
    const displayName = getCategoryDisplayName(categoryId);
    if (displayName && displayName !== 'Unknown') {
      return displayName;
    }
  }
  
  // Fallback to stored name/label
  const rawName = envelope.name || envelope.label || categoryId || '';
  if (!rawName) return 'Unnamed Envelope';
  
  // If the name contains hyphens or underscores (like category IDs), convert to Title Case
  if (rawName.includes('-') || rawName.includes('_')) {
    return toTitleCase(rawName.replace(/[-_]/g, ' '));
  }
  
  // Otherwise, ensure each word is capitalized
  return toTitleCase(rawName);
}

/**
 * EnvelopeSelector
 * @param {Object} props
 * @param {string} props.selectedEnvelopeId - Currently selected envelope ID
 * @param {Function} props.onChange - Callback when envelope is selected: (envelope) => void
 * @param {string} props.placeholder - Placeholder text when nothing selected
 * @param {boolean} props.disabled - Disable the selector
 * @param {string} props.error - Error message to display
 * @param {boolean} props.required - Whether selection is required
 * @param {string} props.className - Additional CSS classes
 */
export default function EnvelopeSelector({
  selectedEnvelopeId = null,
  onChange,
  placeholder = 'Select budget envelope...',
  disabled = false,
  error = null,
  required = false,
  className = ''
}) {
  const navigate = useNavigate();
  const { currency } = useAuth();
  const currencySymbol = getCurrencySymbol(currency);
  
  // Envelope data from context
  const { envelopes, isLoading, budgetId, refresh } = useBudgetEnvelopes();
  const { searchEnvelopes, allEnvelopes } = useEnvelopeSearch();
  
  // Debug: Log envelope state on mount and updates
  useEffect(() => {
    console.log('[EnvelopeSelector] State:', { 
      envelopeCount: envelopes?.length || 0, 
      isLoading, 
      budgetId: budgetId || 'none'
    });
  }, [envelopes, isLoading, budgetId]);
  
  // Local state
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // Refs
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Get currently selected envelope
  const selectedEnvelope = useMemo(() => {
    if (!selectedEnvelopeId) return null;
    return envelopes.find(e => e.id === selectedEnvelopeId || e.envelopeId === selectedEnvelopeId);
  }, [selectedEnvelopeId, envelopes]);

  // Filter envelopes based on search
  const filteredEnvelopes = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) {
      return envelopes;
    }
    return searchEnvelopes(searchQuery);
  }, [searchQuery, envelopes, searchEnvelopes]);

  // Group envelopes by category type for better organization
  const groupedEnvelopes = useMemo(() => {
    const groups = {
      needs: { label: 'Needs', envelopes: [] },
      wants: { label: 'Wants', envelopes: [] },
      savings: { label: 'Savings', envelopes: [] },
      other: { label: 'Other', envelopes: [] }
    };
    
    filteredEnvelopes.forEach(env => {
      const category = (env.category || env.categoryKey || '').toLowerCase();
      
      // Categorize based on common patterns
      if (['housing', 'utilities', 'groceries', 'transportation', 'healthcare', 'insurance', 'debt'].some(c => category.includes(c))) {
        groups.needs.envelopes.push(env);
      } else if (['entertainment', 'dining', 'shopping', 'personal', 'subscriptions'].some(c => category.includes(c))) {
        groups.wants.envelopes.push(env);
      } else if (['savings', 'goals', 'emergency', 'investment'].some(c => category.includes(c))) {
        groups.savings.envelopes.push(env);
      } else {
        groups.other.envelopes.push(env);
      }
    });
    
    // Only return groups that have envelopes
    return Object.values(groups).filter(g => g.envelopes.length > 0);
  }, [filteredEnvelopes]);

  // Flatten for keyboard navigation
  const flatEnvelopes = useMemo(() => {
    return groupedEnvelopes.flatMap(g => g.envelopes);
  }, [groupedEnvelopes]);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, flatEnvelopes.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatEnvelopes[highlightedIndex]) {
          handleSelect(flatEnvelopes[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        break;
      default:
        break;
    }
  }, [isOpen, flatEnvelopes, highlightedIndex]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle envelope selection
  const handleSelect = (envelope) => {
    onChange?.(envelope);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(0);
  };

  // Navigate to Budget page to create envelope
  const handleGoToBudget = () => {
    toast('Redirecting to Budget page to create an envelope...', { icon: '📊' });
    navigate('/budget');
    setIsOpen(false);
  };

  // Format remaining budget
  const formatRemaining = (envelope) => {
    // Support multiple field naming conventions from different sources
    const budgetAmount = envelope.budgetedAmount || envelope.amount || envelope.planned || 0;
    const spentAmount = envelope.actualSpent || envelope.actual || envelope.spent || 0;
    const remaining = envelope.remaining ?? (budgetAmount - spentAmount);
    const isOverBudget = remaining < 0;
    
    return {
      value: formatCurrency(Math.abs(remaining), currency),
      isOverBudget,
      percentage: budgetAmount > 0 ? Math.round((spentAmount / budgetAmount) * 100) : 0
    };
  };

  // Render envelope item
  const renderEnvelopeItem = (envelope, index, globalIndex) => {
    const isSelected = selectedEnvelopeId === envelope.id || selectedEnvelopeId === envelope.envelopeId;
    const isHighlighted = globalIndex === highlightedIndex;
    const remainingInfo = formatRemaining(envelope);
    
    return (
      <motion.div
        key={envelope.id || envelope.envelopeId}
        data-index={globalIndex}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.02 }}
        className={`
          flex items-center justify-between px-3 py-2.5 cursor-pointer rounded-lg mx-1
          transition-colors duration-150
          ${isSelected ? 'bg-teal-50 border border-teal-200' : ''}
          ${isHighlighted && !isSelected ? 'bg-gray-100' : ''}
          ${!isSelected && !isHighlighted ? 'hover:bg-gray-50' : ''}
        `}
        onClick={() => handleSelect(envelope)}
        role="option"
        aria-selected={isSelected}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {/* Envelope icon with color indicator */}
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: envelope.color ? `${envelope.color}20` : '#E0F2F1' }}
          >
            <Wallet 
              size={16} 
              style={{ color: envelope.color || '#009688' }} 
            />
          </div>
          
          {/* Name and subcategory */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {formatEnvelopeName(envelope)}
            </p>
            {envelope.subcategory && (
              <p className="text-xs text-gray-500 truncate">{toTitleCase(envelope.subcategory)}</p>
            )}
          </div>
        </div>
        
        {/* Budget info */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <div className="text-right">
            <p className={`text-xs font-medium ${remainingInfo.isOverBudget ? 'text-red-600' : 'text-teal-600'}`}>
              {remainingInfo.isOverBudget ? '-' : ''}{remainingInfo.value}
            </p>
            <p className="text-xs text-gray-400">
              {remainingInfo.percentage}% used
            </p>
          </div>
          
          {isSelected && (
            <Check size={16} className="text-teal-600 flex-shrink-0" />
          )}
        </div>
      </motion.div>
    );
  };

  // Render no envelopes state
  const renderNoEnvelopes = () => (
    <div className="p-4 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
        <AlertCircle size={24} className="text-amber-600" />
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1">No Budget Envelopes</p>
      <p className="text-xs text-gray-500 mb-3">
        Create budget envelopes on the Budget page first.
      </p>
      <div className="flex justify-center gap-2">
        {refresh && (
          <button
            type="button"
            onClick={() => {
              console.log('[EnvelopeSelector] Manual refresh triggered');
              refresh();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        )}
        <button
          type="button"
          onClick={handleGoToBudget}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-100 hover:bg-teal-200 rounded-lg transition-colors"
        >
          <Plus size={14} />
          Go to Budget Page
        </button>
      </div>
    </div>
  );

  // Render no search results
  const renderNoSearchResults = () => (
    <div className="p-4 text-center">
      <p className="text-sm text-gray-500">
        No envelopes match "{searchQuery}"
      </p>
      <p className="text-xs text-gray-400 mt-1">
        Try a different search or create an envelope on the Budget page.
      </p>
    </div>
  );

  return (
    <div 
      ref={containerRef} 
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Selected value display / trigger */}
      <button
        type="button"
        ref={inputRef}
        disabled={disabled || isLoading}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2.5
          bg-white border rounded-xl text-left
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:border-teal-300'}
          ${isOpen ? 'border-teal-500 ring-2 ring-teal-100' : 'border-gray-300'}
          ${error ? 'border-red-300 ring-2 ring-red-100' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedEnvelope?.name || placeholder}
      >
        {selectedEnvelope ? (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: selectedEnvelope.color ? `${selectedEnvelope.color}20` : '#E0F2F1' }}
            >
              <Wallet 
                size={14} 
                style={{ color: selectedEnvelope.color || '#009688' }} 
              />
            </div>
            <span className="text-sm font-medium text-gray-900 truncate">
              {formatEnvelopeName(selectedEnvelope)}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">
            {isLoading ? 'Loading envelopes...' : placeholder}
          </span>
        )}
        
        <ChevronDown 
          size={18} 
          className={`text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}

      {/* Required indicator */}
      {required && !selectedEnvelope && !error && (
        <p className="mt-1 text-xs text-gray-400">* Required</p>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
            style={{ maxHeight: '320px' }}
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  placeholder="Search envelopes..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-100"
                  autoFocus
                />
              </div>
            </div>

            {/* Envelope list */}
            <div 
              ref={listRef}
              className="overflow-y-auto py-1"
              style={{ maxHeight: '250px' }}
              role="listbox"
            >
              {isLoading ? (
                <div className="p-4 text-center">
                  <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-xs text-gray-400 mt-2">Loading envelopes...</p>
                </div>
              ) : envelopes.length === 0 ? (
                renderNoEnvelopes()
              ) : filteredEnvelopes.length === 0 ? (
                renderNoSearchResults()
              ) : (
                groupedEnvelopes.map((group, groupIndex) => {
                  // Calculate global start index for this group
                  const globalStartIndex = groupedEnvelopes
                    .slice(0, groupIndex)
                    .reduce((sum, g) => sum + g.envelopes.length, 0);
                  
                  return (
                    <div key={group.label}>
                      {/* Group header */}
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                        {group.label}
                      </div>
                      
                      {/* Group items */}
                      {group.envelopes.map((envelope, index) => 
                        renderEnvelopeItem(envelope, index, globalStartIndex + index)
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer - Go to Budget */}
            {envelopes.length > 0 && (
              <div className="p-2 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={handleGoToBudget}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <Plus size={14} />
                  Create new envelope on Budget page
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * EnvelopeSelectorCompact
 * A more compact version for inline use in tables or lists
 * 
 * FINLIST CFO MODEL: This links Goals to Budget Envelopes
 * - Requires an active budget with envelope lines
 * - If no budget exists, shows helpful message
 */
export function EnvelopeSelectorCompact({
  selectedEnvelopeId = null,
  onChange,
  disabled = false,
  className = ''
}) {
  const { envelopes, isLoading, budgetId } = useBudgetEnvelopes();
  
  const selectedEnvelope = useMemo(() => {
    if (!selectedEnvelopeId) return null;
    return envelopes.find(e => e.id === selectedEnvelopeId || e.envelopeId === selectedEnvelopeId);
  }, [selectedEnvelopeId, envelopes]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={`text-sm text-gray-400 px-2 py-1.5 border border-gray-200 rounded-lg bg-gray-50 ${className}`}>
        Loading envelopes...
      </div>
    );
  }

  // No budget or no envelopes - show helpful message
  if (!budgetId || envelopes.length === 0) {
    return (
      <div className={`text-sm text-amber-600 px-2 py-1.5 border border-amber-200 rounded-lg bg-amber-50 ${className}`}>
        {!budgetId 
          ? '⚠️ Create a budget first to link envelopes'
          : '⚠️ No envelopes in current budget'
        }
      </div>
    );
  }

  return (
    <select
      value={selectedEnvelopeId || ''}
      onChange={(e) => {
        const envelope = envelopes.find(env => env.id === e.target.value || env.envelopeId === e.target.value);
        onChange?.(envelope || null);
      }}
      disabled={disabled}
      className={`
        text-sm border border-gray-200 rounded-lg px-2 py-1.5
        focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-100
        ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}
        ${className}
      `}
    >
      <option value="">Select envelope...</option>
      {envelopes.map(env => (
        <option key={env.id || env.envelopeId} value={env.id || env.envelopeId}>
          {formatEnvelopeName(env)}
        </option>
      ))}
    </select>
  );
}

/**
 * EnvelopeBadge
 * Display a selected envelope as a badge
 */
export function EnvelopeBadge({
  envelopeId,
  onClear,
  showBalance = false,
  className = ''
}) {
  const { envelopes } = useBudgetEnvelopes();
  const { currency } = useAuth();
  
  const envelope = useMemo(() => {
    if (!envelopeId) return null;
    return envelopes.find(e => e.id === envelopeId || e.envelopeId === envelopeId);
  }, [envelopeId, envelopes]);

  if (!envelope) return null;

  // Support multiple field naming conventions from different sources
  const budgetAmount = envelope.budgetedAmount || envelope.amount || envelope.planned || 0;
  const spentAmount = envelope.actualSpent || envelope.actual || envelope.spent || 0;
  const remaining = envelope.remaining ?? (budgetAmount - spentAmount);
  const isOverBudget = remaining < 0;

  return (
    <div 
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium
        bg-teal-50 text-teal-700 border border-teal-200
        ${className}
      `}
    >
      <Wallet size={12} />
      <span>{formatEnvelopeName(envelope)}</span>
      
      {showBalance && (
        <span className={`ml-1 ${isOverBudget ? 'text-red-600' : 'text-teal-600'}`}>
          ({formatCurrency(remaining, currency)})
        </span>
      )}
      
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="ml-1 hover:bg-teal-100 rounded p-0.5"
          aria-label="Clear envelope selection"
        >
          ×
        </button>
      )}
    </div>
  );
}
